import { type ArticleFromQueryArray } from "@lib/types";
import Image from "next/image";
import Link from "next/link";

import { Badge } from "@/components/ui/badge";
import { urlForImageCached } from "@/sanity/lib/utils";

import { Button } from "../ui/button";
import { capitalizeFirst, formatDate, getArticleUrl } from "./utils";

interface FeaturedArticleProps {
  article: ArticleFromQueryArray;
}

function ReadMoreButton({ article }: { article: ArticleFromQueryArray }) {
  const url = getArticleUrl(article);

  return (
    <Link href={url}>
      <Button variant="secondary">Read More</Button>
    </Link>
  );

  return (
    <div className="border-neutral outline-neutral inline-flex h-16 items-center justify-center gap-2 rounded-lg px-6 py-4 outline-2">
      <Link href={url} className="typography-text-medium-normal text-white no-underline">
        Read More
      </Link>
    </div>
  );
}

export function FeaturedArticle({ article }: FeaturedArticleProps) {
  if (!article) return null;

  return (
    <section>
      <div className="border-indigo-lighter p-padding-element-lg tablet:p-padding-section-sm tablet:flex-row relative flex flex-col gap-12 rounded-xl border">
        {/* Gradient Background Overlay */}
        <div className="absolute inset-0 z-0 rounded-xl bg-[linear-gradient(180deg,rgba(189,212,252,0.2),rgba(178,178,178,0.2))]" />
        {/* Dark Background with Blend Mode */}
        <div className="bg-neutral-darkest absolute inset-0 z-0 rounded-xl opacity-5 mix-blend-soft-light" />

        {/* Left Column - Content */}
        <div className="relative z-1 flex flex-1 flex-col items-start justify-center gap-6">
          {/* Badge and Read Time */}
          <div className="flex items-center justify-start gap-4">
            <Badge
              variant="outline"
              className="text-foreground typography-text-medium-normal rounded-[100px] border-[1.5px] border-white bg-transparent px-3 py-1 text-xl"
            >
              {capitalizeFirst(article._type)}
            </Badge>
            <span className="typography-text-medium-medium text-foreground text-xl">
              5 min read
            </span>
          </div>

          {/* Title and Description */}
          <div className="flex flex-col items-start justify-start gap-6">
            <h2 className="typography-heading-4 max-w-[580px] text-white">{article.title}</h2>
            {article.summary ? (
              <p className="typography-text-medium-normal max-w-[580px] text-white">
                {article.summary}
              </p>
            ) : null}

            {/* Topic Tags and Date */}
            <div className="flex items-start justify-start gap-2.5">
              {article.publishedAt ? (
                <time
                  dateTime={article.publishedAt}
                  className="typography-text-medium-normal text-neutral"
                >
                  {formatDate(article.publishedAt)}
                </time>
              ) : null}
            </div>
          </div>

          {/* Read More Button */}
          <ReadMoreButton article={article} />
        </div>

        {/* Right Column - Image */}
        <div className="tablet:order-last relative z-1 order-first flex flex-1 flex-col items-start justify-start gap-4">
          <div className="bg-emerald-darkest relative aspect-video w-full overflow-hidden rounded-xl">
            {/* Trending Badge */}
            <Badge
              variant="success"
              className="text-emerald absolute top-4 left-[15px] z-10 px-4 py-2 text-xl"
            >
              Trending
            </Badge>
            {/* Image */}
            {article.image?.image ? (
              <Image
                src={urlForImageCached(article.image.image)?.width(800).url() || ""}
                alt={article.image.alt || article.title}
                fill
                className="object-cover"
              />
            ) : (
              <div className="h-full w-full bg-neutral-800" />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
